import { z } from "zod";

/**
 * Observed lead-time grains: the aggregation levels the procurement lead-time
 * ladder measures, ordered most-specific first. Single source of truth shared
 * by the backend rung registry (forecasting/observedLeadTimes.ts) and any RPC
 * contract that surfaces rung breakdowns, so adding a rung is one list edit on
 * both sides instead of a scattered enum hunt.
 */
export const LEAD_TIME_GRAINS = ["item_loc", "item", "item_national"] as const;

/** Aggregation level of one observed lead-time stats row. */
export type LeadTimeGrain = (typeof LEAD_TIME_GRAINS)[number];

export function isLeadTimeGrain(value: string): value is LeadTimeGrain {
  return (LEAD_TIME_GRAINS as readonly string[]).includes(value);
}

/**
 * `im_processing_recipes.operation`. Descriptive only: every operation is the
 * same make edge to the planner. Mirrored by the table's operation CHECK.
 */
export const PROCESSING_RECIPE_OPERATIONS = [
  "cut",
  "repack",
  "assemble",
  "mill",
] as const;

export type ProcessingRecipeOperation =
  (typeof PROCESSING_RECIPE_OPERATIONS)[number];

/** `im_product_substitutes.source`. Mirrored by the table's source CHECK. */
export const PRODUCT_SUBSTITUTE_SOURCES = [
  "supersede",
  "approved_alternate",
  "spec_match",
] as const;

export type ProductSubstituteSource =
  (typeof PRODUCT_SUBSTITUTE_SOURCES)[number];

/**
 * `im_replenishment_suggestion_option_legs.leg_type` (app-enforced, no DB
 * constraint). `make` and `substitute` legs come from the fulfillment graph's
 * make and substitute edges.
 */
export const OPTION_LEG_TYPES = [
  "buy",
  "transfer",
  "stock",
  "make",
  "substitute",
] as const;

export type OptionLegType = (typeof OPTION_LEG_TYPES)[number];

/**
 * How an option fills its line. `direct` buys or moves the line's own product;
 * `substitute` and `make` legs carry other products (the supplying product, or
 * the make's components), so the line's in-place edits do not apply to them.
 */
export const OPTION_PLAN_KINDS = ["direct", "substitute", "make"] as const;

export type OptionPlanKind = (typeof OPTION_PLAN_KINDS)[number];

/** A substitute outranks a make: its trailing leg decides the line's product. */
export function optionPlanKind(legTypes: readonly string[]): OptionPlanKind {
  if (legTypes.includes("substitute")) return "substitute";
  if (legTypes.includes("make")) return "make";
  return "direct";
}

/**
 * Manual lead-time override scopes (`im_lead_time_overrides.scope`), ordered
 * most-specific first: registry order is resolution precedence, the same
 * convention as LEAD_TIME_GRAINS. v1 ships `item_loc` (one SKU x warehouse)
 * only; coarser scopes (item-wide, supplier, supplier x item, manufacturer)
 * are added here, in LEAD_TIME_OVERRIDE_SCOPE_DIMENSIONS, and in the table's
 * scope CHECK constraint when asked for. Plan:
 * docs/procurement/lead-time-overrides.md.
 */
export const LEAD_TIME_OVERRIDE_SCOPES = ["item_loc"] as const;

export type LeadTimeOverrideScope = (typeof LEAD_TIME_OVERRIDE_SCOPES)[number];

export function isLeadTimeOverrideScope(
  value: string,
): value is LeadTimeOverrideScope {
  return (LEAD_TIME_OVERRIDE_SCOPES as readonly string[]).includes(value);
}

/**
 * The dimensions an override scope can key on. `product` is the ProductV3 key
 * (the SKU; procurement carries no legacy v2 identity); `supplier` is reserved
 * for the future supplier scopes and is never set in v1. A row carries exactly
 * its scope's dimensions and nothing else (a warehouse's division is reached
 * through the warehouse, not stored on the row).
 */
export const LEAD_TIME_OVERRIDE_DIMENSIONS = [
  "product",
  "warehouse",
  "supplier",
] as const;

export type LeadTimeOverrideDimension =
  (typeof LEAD_TIME_OVERRIDE_DIMENSIONS)[number];

/**
 * Which dimensions each scope keys on. Typed as a Record over the scope list
 * so adding a scope without declaring its dimensions fails to compile. The
 * backend derives its dimension validation and lookup keys from this; the UI
 * derives the scope picker.
 */
export const LEAD_TIME_OVERRIDE_SCOPE_DIMENSIONS: Record<
  LeadTimeOverrideScope,
  readonly LeadTimeOverrideDimension[]
> = {
  item_loc: ["product", "warehouse"],
};

/**
 * Bounds of a manual override's lead time, whole calendar days. The same
 * clamp the observed resolver applies to measured values; the table's CHECK
 * constraint, the contract, and the service all enforce it.
 */
export const LEAD_TIME_OVERRIDE_DAYS_MIN = 1;
export const LEAD_TIME_OVERRIDE_DAYS_MAX = 365;

/** Source tag of a lead time pinned by a manual override. */
export const LEAD_TIME_OVERRIDE_SOURCE = "override" as const;

/**
 * Every value a lead-time resolution's `source` can take: a measured rung, a
 * manual override, or "none" when nothing answered. Contracts derive their
 * enum from this list.
 */
export const LEAD_TIME_SOURCES = [
  ...LEAD_TIME_GRAINS,
  LEAD_TIME_OVERRIDE_SOURCE,
  "none",
] as const;

/**
 * Origin of a lead-time resolution: a measured rung, an active manual
 * override, or "none" when neither answered. "none" carries no number;
 * consumers apply their own configured fallback and read surfaces show "no
 * estimate" instead of a fabricated value.
 */
export type LeadTimeSource = (typeof LEAD_TIME_SOURCES)[number];

/**
 * Forecast growth/shrinkage factor scopes
 * (`im_forecast_adjustment_factors.scope`), ordered most-specific first:
 * registry order is resolution precedence, the same convention as
 * LEAD_TIME_OVERRIDE_SCOPES. v1 ships `item_loc` (one SKU x warehouse) only;
 * coarser scopes (item-wide, division, category) are added here, in
 * FORECAST_FACTOR_SCOPE_DIMENSIONS, and in the table's scope CHECK constraint
 * when asked for. A separate list from the lead-time one because the two
 * kinds will grow different dimension sets. Plan:
 * docs/procurement/growth-shrinkage-factors.md.
 */
export const FORECAST_FACTOR_SCOPES = ["item_loc"] as const;

export type ForecastFactorScope = (typeof FORECAST_FACTOR_SCOPES)[number];

export function isForecastFactorScope(
  value: string,
): value is ForecastFactorScope {
  return (FORECAST_FACTOR_SCOPES as readonly string[]).includes(value);
}

/**
 * The dimensions a factor scope can key on. `product` is the ProductV3 key
 * (the SKU; procurement carries no legacy v2 identity). A row carries exactly
 * its scope's dimensions and nothing else (a warehouse's division is reached
 * through the warehouse, not stored on the row).
 */
export const FORECAST_FACTOR_DIMENSIONS = ["product", "warehouse"] as const;

export type ForecastFactorDimension =
  (typeof FORECAST_FACTOR_DIMENSIONS)[number];

/**
 * Which dimensions each factor scope keys on. Typed as a Record over the scope
 * list so adding a scope without declaring its dimensions fails to compile.
 */
export const FORECAST_FACTOR_SCOPE_DIMENSIONS: Record<
  ForecastFactorScope,
  readonly ForecastFactorDimension[]
> = {
  item_loc: ["product", "warehouse"],
};

/**
 * Bounds of a growth/shrinkage factor. Multiplicative: 1.15 plans 15% above
 * the statistical forecast, 0.90 plans 10% below. Exactly 1 is rejected as
 * well (a no-op row that would only clutter the audit trail). The table's
 * CHECK constraint, the contract, and the service all enforce the same rule.
 */
export const FORECAST_FACTOR_MIN = 0.01;
export const FORECAST_FACTOR_MAX = 100;

/**
 * Key under which a replenishment option leg's JSONB `metadata` carries its
 * all-units price-break provenance. One key, one shape, written by the engine
 * and read by the board contracts.
 */
export const LEG_PRICE_BREAK_METADATA_KEY = "priceBreak" as const;

/**
 * The rungs of a buy leg's price ladder, highest precedence first
 * (forward-buying plan, Part 2, decisions 15 and 17): a supplier cost
 * agreement, then the supplier product row's price breaks, then its list cost.
 */
export const LEG_COST_SOURCES = ["agreement", "price_break", "list"] as const;

export const LegCostSourceSchema = z.enum(LEG_COST_SOURCES);

export type LegCostSource = z.infer<typeof LegCostSourceSchema>;

/** The supplier product rungs: what prices a buy when no agreement does. */
export const SupplierProductCostSourceSchema = LegCostSourceSchema.exclude([
  "agreement",
]);

export type SupplierProductCostSource = z.infer<
  typeof SupplierProductCostSourceSchema
>;

/**
 * What the engine paid attention to when it priced a buy leg, frozen at
 * suggestion time.
 *
 * - `tier` / `unitCost`: the reached price-break tier and the unit cost it
 *   bought at. `tier` is null when no break applied and the leg bought at the
 *   supplier's list cost.
 * - `nextTier*`: the nearest higher break boundary, as the vendor quotes it.
 *   All four are null when the leg is already at the vendor's deepest break.
 * - `savingsAtNextTier` is an all-units comparison: what the WHOLE line costs
 *   at `nextTierMinQuantity` priced at the current tier, minus its cost at the
 *   next tier. It can be zero or negative when a vendor's break table is not
 *   monotonic.
 * - `nudge*`: what the board actually offers the buyer ("+5 units reaches
 *   $8.00, saves $50"). A boundary is not always a quantity the vendor ships:
 *   `nudgeQuantity` is `nextTierMinQuantity` rounded up to the vendor's MOQ and
 *   order multiple, `nudgeUnitCost` is the effective cost at that rounded
 *   quantity (which can be a deeper tier than the next one when the rounding
 *   crosses another boundary), and `nudgeSavings` is the all-units saving at
 *   `nudgeQuantity` against buying that same quantity at the line's current
 *   tier. All three are null when there is no next tier or the saving is not
 *   strictly positive, so a non-null `nudgeQuantity` is always worth showing.
 * - `supplierProductId`: the `ap_supplier_products` row every other field was
 *   priced from. A vendor can quote one SKU on several rows (a general one and
 *   one per ship-to), each with its own list cost and breaks, and the engine
 *   buys on whichever is cheapest. Applying the nudge re-prices that row and no
 *   other, so the buyer gets the offer the board showed or a refusal.
 *
 * - `costSource`: which rung of the price ladder set `unitCost`
 *   (forward-buying plan, Part 2, decision 17): a supplier cost `agreement`
 *   valid on the run's placement date, the supplier product row's
 *   `price_break`, or that row's `list` cost. An agreement outranks a price
 *   break, which outranks list, whatever they cost.
 * - `costAgreementId` / `contractNumber` / `constraintEndsOn`: the winning
 *   `ap_supplier_cost_agreements` row, its contract number and the last day
 *   of its window (`effective_end`), so the board can say "contract 4046,
 *   ends Sep 30". All three are null unless `costSource` is `agreement`.
 * - `withoutAgreementUnitCost` / `withoutAgreementCostSource`: what the same
 *   quantity costs per unit on the supplier product row when no agreement
 *   applies (its reached price break, else its list cost) and which of the two
 *   that is. The board states an agreement's saving against it ("list $2.46,
 *   saves $720"). Both are null unless `costSource` is `agreement`, and on
 *   every leg priced before they were recorded. The unit cost can be below the
 *   agreement's: an agreement outranks the row whatever each costs.
 *
 * Every field is nullable so a leg persisted before this shape existed, or one
 * a buyer composed by hand, resolves cleanly instead of failing validation. The
 * `nudge*` fields and `supplierProductId` also default to null, because legs
 * frozen before they existed carry the next-tier fields without them and must
 * still parse; with no lattice or no row recorded they simply offer no nudge.
 *
 * LEGACY `costSource`: a leg frozen before cost agreements were priced carries
 * no `costSource`. Every such leg was priced on its supplier product row, so
 * {@link extractLegPriceBreak}, the one reader of stored legs, fills it in from
 * `tier` ({@link legacyLegCostSource}): `price_break` when the leg reached a
 * tier, `list` when it did not. The schema's own default, `price_break`, only
 * applies to a blob parsed without that reader; it names the same supplier
 * product ladder and never claims an agreement.
 */
export const LegPriceBreakSchema = z.object({
  tier: z.number().nullable(),
  unitCost: z.number().nullable(),
  nextTierMinQuantity: z.number().nullable(),
  nextTierUnitCost: z.number().nullable(),
  unitsToNextTier: z.number().nullable(),
  savingsAtNextTier: z.number().nullable(),
  nudgeQuantity: z.number().nullable().default(null),
  nudgeUnitCost: z.number().nullable().default(null),
  nudgeSavings: z.number().nullable().default(null),
  supplierProductId: z.string().uuid().nullable().default(null),
  costSource: LegCostSourceSchema.default("price_break"),
  costAgreementId: z.string().uuid().nullable().default(null),
  contractNumber: z.number().int().nullable().default(null),
  constraintEndsOn: z.iso.date().nullable().default(null),
  withoutAgreementUnitCost: z.number().nullable().default(null),
  withoutAgreementCostSource:
    SupplierProductCostSourceSchema.nullable().default(null),
});

export type LegPriceBreak = z.infer<typeof LegPriceBreakSchema>;

/**
 * The cost-source half of {@link LegPriceBreak}: which rung of the price
 * ladder priced a buy, and the agreement behind it when there is one. The
 * engine's `BuyOption` carries these flat, next to its unit cost.
 */
export type LegCostProvenance = Pick<
  LegPriceBreak,
  "costSource" | "costAgreementId" | "contractNumber" | "constraintEndsOn"
>;

/**
 * The cost source of a leg priced before cost sources were recorded. Such a
 * leg was priced on its supplier product row: at a price break when it reached
 * a tier, at the row's list cost when it did not.
 */
export function legacyLegCostSource(tier: number | null): LegCostSource {
  return tier === null ? "list" : "price_break";
}

/**
 * A buy's cost provenance, read off its price-break provenance, or, for a leg
 * that recorded none (a hand-built plan), the legacy reading of its tier.
 */
export function legCostProvenanceOf(
  priceBreak: LegPriceBreak | null,
  tier: number | null,
): LegCostProvenance {
  if (priceBreak !== null) {
    return {
      costSource: priceBreak.costSource,
      costAgreementId: priceBreak.costAgreementId,
      contractNumber: priceBreak.contractNumber,
      constraintEndsOn: priceBreak.constraintEndsOn,
    };
  }
  return {
    costSource: legacyLegCostSource(tier),
    costAgreementId: null,
    contractNumber: null,
    constraintEndsOn: null,
  };
}

/**
 * The contract named in an option rationale's cost-source clause. Either field
 * can be null: the clause names what the provenance recorded, no more.
 */
export interface RationaleCostAgreement {
  contractNumber: number | null;
  /** `YYYY-MM-DD`, the agreement's last valid day. */
  constraintEndsOn: string | null;
}

const COST_SOURCE_CLAUSE_PREFIX = "; priced by ";

/**
 * The cost-source clause as the writers below append it. Anchored at the end
 * of the rationale, because the clause is always the last thing added.
 */
const COST_SOURCE_CLAUSE =
  /; priced by (?:contract (\d+)|a contract)(?:, ends (\d{4}-\d{2}-\d{2}))?$/;

/**
 * An option rationale with the contract that priced its buy appended
 * (forward-buying plan, Part 2, decision 17):
 * "no_surplus_buy; priced by contract 4046, ends 2026-09-30". Anything not
 * priced by a cost agreement comes back unchanged. The rationale's own text
 * stays first and intact, so anything reading a reason code still finds it.
 *
 * Phase 4 and the forward-buy pass both write the clause with this function
 * and the board reads it back with {@link splitCostSourceClause}, so the
 * format lives in one place.
 */
export function withCostSourceClause(
  rationale: string,
  provenance: LegCostProvenance,
): string {
  if (provenance.costSource !== "agreement") return rationale;
  const contract =
    provenance.contractNumber === null
      ? "a contract"
      : `contract ${provenance.contractNumber}`;
  const ends =
    provenance.constraintEndsOn === null
      ? ""
      : `, ends ${provenance.constraintEndsOn}`;
  return `${rationale}${COST_SOURCE_CLAUSE_PREFIX}${contract}${ends}`;
}

/**
 * An option rationale split into its own text and the contract its
 * cost-source clause names, the inverse of {@link withCostSourceClause}.
 * `agreement` is null when the rationale carries no such clause, and
 * `reason` is then the whole rationale.
 */
export function splitCostSourceClause(rationale: string): {
  reason: string;
  agreement: RationaleCostAgreement | null;
} {
  const match = COST_SOURCE_CLAUSE.exec(rationale);
  if (match === null) return { reason: rationale, agreement: null };
  const [, contractNumber, constraintEndsOn] = match;
  return {
    reason: rationale.slice(0, match.index),
    agreement: {
      contractNumber:
        contractNumber === undefined ? null : Number(contractNumber),
      constraintEndsOn: constraintEndsOn ?? null,
    },
  };
}

/**
 * `data.code` on the CONFLICT the price-break apply throws when the offer the
 * board showed can no longer be honored. The board dismisses that option's
 * nudge until the next engine run instead of treating it as a failure.
 */
export const PRICE_BREAK_OFFER_CHANGED_CODE =
  "PRICE_BREAK_OFFER_CHANGED" as const;

/**
 * Why the shown offer no longer holds: the leg names no supplier-product row
 * (frozen before the row was recorded), that row is gone or no longer quotes
 * the line, the ladder re-priced today yields a different nudge, or the cost
 * agreement the leg was priced on is no longer eligible on today's placement
 * date or no longer prices the leg's quantity at the tier it recorded
 * (forward-buying plan, Part 2, decision 18).
 */
export const PriceBreakOfferStaleReasonSchema = z.enum([
  "offer_unpinned",
  "offer_withdrawn",
  "offer_changed",
  "agreement_changed",
]);

export type PriceBreakOfferStaleReason = z.infer<
  typeof PriceBreakOfferStaleReasonSchema
>;

export const PriceBreakOfferChangedDataSchema = z.object({
  code: z.literal(PRICE_BREAK_OFFER_CHANGED_CODE),
  reason: PriceBreakOfferStaleReasonSchema,
});

/**
 * Pull the price-break provenance out of an option leg's `metadata` blob.
 * Returns null when the blob is missing, carries no `priceBreak` key (every
 * leg written before this shipped), or holds something that is not the shape
 * above.
 */
export function extractLegPriceBreak(
  metadata: Record<string, unknown> | null | undefined,
): LegPriceBreak | null {
  if (!metadata || typeof metadata !== "object") return null;
  const parsed = LegPriceBreakSchema.safeParse(
    withLegacyCostSource(metadata[LEG_PRICE_BREAK_METADATA_KEY]),
  );
  return parsed.success ? parsed.data : null;
}

/**
 * A stored blob with no `costSource` was frozen before cost sources were
 * recorded; name its source from its tier (see {@link legacyLegCostSource}).
 * Anything else passes through for the schema to judge.
 */
function withLegacyCostSource(stored: unknown): unknown {
  if (stored === null || typeof stored !== "object" || Array.isArray(stored)) {
    return stored;
  }
  if ("costSource" in stored) return stored;
  const tier = "tier" in stored ? stored.tier : null;
  return {
    ...stored,
    costSource: legacyLegCostSource(typeof tier === "number" ? tier : null),
  };
}

/**
 * Forward buying: buying ahead of need until a vendor cart clears the vendor's
 * minimum order value or its prepaid-freight threshold, by extending the
 * coverage of lines already in the cart and by pulling forward the vendor's
 * other stocked SKUs that will need an order soon. Plan:
 * docs/procurement/forward-buying.md.
 *
 * Coverage is always counted in DAYS AFTER THE ORDER ARRIVES (decision 1), and
 * the cap is an INCREMENT over what each line already covers: an ordinary order
 * goes up to the policy's order-up-to level, which at Rexel holds two to four
 * months, so a total-days cap would sit below where most orders already land.
 */
export const FORWARD_BUY_EXTRA_COVERAGE_CAP_DAYS = 60;

/** One rung of the round-robin: how much coverage a single step adds. */
export const FORWARD_BUY_COVERAGE_STEP_DAYS = 30;

/**
 * How soon after the cart's order arrives a SKU with no open need must be
 * projected to fall below its safety stock for the pass to pull its next order
 * forward into the cart (decision 15). A SKU that would not need an order
 * within this window is not bought early.
 */
export const FORWARD_BUY_PULL_FORWARD_WINDOW_DAYS = 60;

/**
 * Popularity codes (`im_inventory_policies.popularity_code`, the 2nd character
 * of INITINFC `FO1`) the pass will forward-buy. A is the fastest mover, E is
 * non-stock. Deficit detection already runs A through D; forward buying is
 * deliberately narrower, because buying months of a C or D mover to clear a
 * vendor minimum is how a branch ends up sitting on it. NULL (unknown) is out.
 */
export const FORWARD_BUY_POPULARITY_ALLOW = ["A", "B"] as const;

/**
 * Which cart-level threshold the uplift was chasing: the vendor's hard minimum
 * order value (`ap_supplier_warehouse_constraints.constraint_uom = '1'`) or the
 * soft prepaid-freight minimum (`'2'`). The pass targets the higher of the two
 * and names whichever one set that target.
 */
export const FORWARD_BUY_CONSTRAINTS = ["mov", "freight"] as const;

export type ForwardBuyConstraint = (typeof FORWARD_BUY_CONSTRAINTS)[number];

/**
 * How a cart came out of the pass (decision 9), for its log line and the run
 * counters only; nothing stores it. Everything other than `cleared` leaves a
 * gap:
 *
 * - `cleared`: the cart reached its target.
 * - `no_eligible_lines`: no line in the cart could be extended and none of the
 *   vendor's other SKUs was due to be pulled forward.
 * - `all_lines_at_cap`: no plan within the coverage cap
 *   ({@link FORWARD_BUY_EXTRA_COVERAGE_CAP_DAYS}) clears the target, so
 *   nothing is applied (clear or nothing).
 * - `no_forecast`: the only candidate lines have no active forecast, and no
 *   SKU was pulled forward.
 */
export const FORWARD_BUY_REASON_CODES = [
  "cleared",
  "no_eligible_lines",
  "all_lines_at_cap",
  "no_forecast",
] as const;

export type ForwardBuyReasonCode = (typeof FORWARD_BUY_REASON_CODES)[number];

/**
 * Key under which a forward-buy uplift option's JSONB `metadata` carries its
 * provenance. The presence of this key is also what marks an option as an
 * uplift, so cleanup and revert both find yesterday's options by it.
 */
export const FORWARD_BUY_METADATA_KEY = "forwardBuy" as const;

/**
 * Everything a buyer (and the next engine run) needs to understand and undo one
 * forward-buy uplift (decision 12).
 *
 * - `pulledForward` marks a line the pass created for a SKU with no open need,
 *   pulling its next order into the cart (decision 15). Its suggestion exists
 *   only for the uplift, so cleanup and removal delete the suggestion instead
 *   of restoring a choice, and its base quantity, base unit cost and baseline
 *   option are 0, 0 and null.
 * - `baselineOptionId` is the option the uplift was copied from: the buyer's
 *   chosen option if there was one, else the rank-1 engine option. Null on a
 *   pulled-forward line.
 * - `priorChosenOptionId` is what `chosen_option_id` held before the uplift
 *   took it, and is null when the suggestion had no buyer choice. Cleanup and
 *   removal restore it, but only while the uplift is still the chosen option.
 * - `baseQuantity` / `upliftQuantity` are the buy leg's quantity before and
 *   after, both on the vendor's MOQ/increment lattice.
 * - `baseUnitCost` is the baseline buy leg's unit cost, frozen when the uplift
 *   was planned. An all-units break reprices the base units too, so the net
 *   spend change is the uplift leg's estimated cost minus
 *   `baseQuantity * baseUnitCost`, not the added units at the new unit cost.
 * - `totalCoverageDays` counts from the order's arrival;
 *   `extraCoverageDays` is what the uplift adds over what the line already
 *   covered (its base order, or the stock on hand for a pulled-forward line),
 *   at most {@link FORWARD_BUY_EXTRA_COVERAGE_CAP_DAYS}.
 * - `priceBreakTier` is the tier the uplifted quantity reached, null when it
 *   still buys at the supplier's list cost.
 */
export const OptionForwardBuySchema = z.object({
  batchId: z.string(),
  pulledForward: z.boolean(),
  baselineOptionId: z.string().nullable(),
  priorChosenOptionId: z.string().nullable(),
  baseQuantity: z.number(),
  upliftQuantity: z.number(),
  baseUnitCost: z.number(),
  totalCoverageDays: z.number(),
  extraCoverageDays: z.number(),
  constraint: z.enum(FORWARD_BUY_CONSTRAINTS),
  priceBreakTier: z.number().nullable(),
});

export type OptionForwardBuy = z.infer<typeof OptionForwardBuySchema>;

/**
 * Pull the uplift provenance out of an option's `metadata` blob. Null when the
 * blob is missing, carries no `forwardBuy` key (every option the engine writes
 * that is not an uplift), or holds something that is not the shape above.
 */
export function extractOptionForwardBuy(
  metadata: Record<string, unknown> | null | undefined,
): OptionForwardBuy | null {
  if (!metadata || typeof metadata !== "object") return null;
  const parsed = OptionForwardBuySchema.safeParse(
    metadata[FORWARD_BUY_METADATA_KEY],
  );
  return parsed.success ? parsed.data : null;
}

/**
 * How a special-cost buy line is received (GEN-2943): at the lower cost on
 * the PO, or at the buyer (system) cost with the variance parked in a GL.
 * Null on a line means the buyer made no choice.
 */
export const PPV_RECEIVE_AT = [
  "po_cost",
  "buyer_cost",
  "cost_constraint",
] as const;

export const PpvReceiveAtSchema = z.enum(PPV_RECEIVE_AT);

export type PpvReceiveAt = z.infer<typeof PpvReceiveAtSchema>;

export const CostConstraintAdjustmentSchema = z.object({
  kind: z.enum(["amount", "savings_percent"]),
  value: z.string().regex(/^\d+(?:\.\d{1,4})?$/),
});
export type CostConstraintAdjustment = z.infer<
  typeof CostConstraintAdjustmentSchema
>;

/**
 * A buyer's terms for one buy line, kept on the replenishment suggestion and
 * copied onto the draft PO line at release. Text fields carry no length cap:
 * a later write to the source system trims to its own limits.
 */
export const LineBuyTermsSchema = z.object({
  /** Unit cost the buyer entered for a one-time special buy; wins over the priced cost. */
  revisedUnitCost: z.number().nonnegative().nullable(),
  /** Contract number that goes on the PO with the revised cost. */
  contractNumber: z.string().nullable(),
  ppvReceiveAt: PpvReceiveAtSchema.nullable(),
  costConstraintUnitCost: z
    .string()
    .regex(/^\d+(?:\.\d{1,4})?$/)
    .nullable()
    .default(null),
  /** Comment that goes on the PO line. */
  poLineComment: z.string().nullable(),
});

export type LineBuyTerms = z.infer<typeof LineBuyTermsSchema>;
